import { ProcessFileType } from '../../convert/enums/process-file-type.enum';
import { ZitadelRole, ZitadelRoleEnum } from '../auth/zitadel';

/**
 * Features that are already shipped but visible only to selected roles for now
 * (internal testing, staged rollout). This is a UI visibility gate, not access control.
 *
 * - Add: new enum value + `internalFeatures` entry; gate UI with `isInternalFeatureEnabled`,
 *   input file types listed in `fileTypes` are hidden from the uploader automatically
 * - Release to everyone: delete the entry and enum value, the compiler points to every call site
 * - Move to a trial/addon: delete the entry and add a rule to the access control system (see xeoRVT)
 */
export enum InternalFeatureEnum {
  NWD_IMPORT = 'nwd-import',
}

export interface InternalFeature {
  /** Roles that see the feature, any of them is enough */
  roles: ZitadelRole[];
  /** Input file types that belong to the feature and are hidden from everyone else */
  fileTypes?: ProcessFileType[];
}

export const internalFeatures: Record<InternalFeatureEnum, InternalFeature> = {
  [InternalFeatureEnum.NWD_IMPORT]: {
    roles: [ZitadelRoleEnum.M_ADMIN],
    fileTypes: [ProcessFileType.NWD, ProcessFileType.NWC],
  },
};

export function isInternalFeatureEnabled(
  feature: InternalFeatureEnum,
  roles: ZitadelRole[],
): boolean {
  return internalFeatures[feature].roles.some((role) => roles.includes(role));
}

/** Input file types hidden from a user with the given roles */
export function getHiddenProcessFileTypes(roles: ZitadelRole[]): ProcessFileType[] {
  return (Object.keys(internalFeatures) as InternalFeatureEnum[])
    .filter((feature) => !isInternalFeatureEnabled(feature, roles))
    .flatMap((feature) => internalFeatures[feature].fileTypes ?? []);
}
